import { useQuery } from '@tanstack/react-query';

import { API_MAX_LIMIT } from '@/constants';
import { ChannelMediaTypeEnum } from '@/features/broadcasters/types';
import { PaginationValues } from '@/hooks/usePagination';
import { mediaManagerClient } from '@/lib/axios';
import { ExtractFnReturnType, QueryConfig } from '@/lib/react-query';
import { PaginatedResponse } from '@/types/api';

import { FiltersProgramsType } from '../../mentions/types';
import { useProgramFilters } from '../store/program-filters';
import { TProgram } from '../types';

type TGetProgramFilters = {
  state?: 'active' | 'inactive' | '';
  priorities?: number[];
  gte?: string;
  lte?: string;
  channel_id?: string;
  channel_media_type?: ChannelMediaTypeEnum | '';
  paginationValues: PaginationValues | null;
  isProgramPage?: boolean;
};

export const getPrograms = async ({
  paginationValues,
  gte,
  lte,
  state,
  priorities,
  channel_id,
  channel_media_type,
  isProgramPage,
}: TGetProgramFilters): Promise<PaginatedResponse<TProgram>> => {
  const { sortBy } = paginationValues || {};

  const sortId = (sortBy?.id as string) || null;
  const sortOrder = (sortBy?.order as string) || 'asc';

  // Warning: If you need order by related fields, you need to apply changes in the backend API (ProgramManager)
  let order_by = 'created_at asc';
  if (sortId) {
    if (sortId === 'schedules') {
      order_by = `start_datetime ${sortOrder}`;
    } else if (sortId === 'channel') {
      order_by = `channel__short_name ${sortOrder}`;
    } else if (sortId === 'language') {
      order_by = `language__name ${sortOrder}`;
    } else if (sortId === 'updated_by') {
      order_by = `updated_at ${sortOrder}`;
    } else {
      order_by = `${sortId} ${sortOrder}`;
    }
  }

  return mediaManagerClient.get('/api/v1/programs', {
    params: {
      ...(isProgramPage && { order_by }),
      ...(paginationValues?.offset && { offset: paginationValues.offset }),
      ...{ limit: paginationValues?.rowsPerPage ?? API_MAX_LIMIT },
      ...(state && { state }),
      ...(channel_media_type && { channel__media_type: channel_media_type }),
      ...(priorities && priorities.length > 0 && { priorities: priorities.join(',') }),
      ...(channel_id && { channel_id }),
      ...(paginationValues?.query && { search: paginationValues?.query }),
      ...(gte && { gte }),
      ...(lte && { lte }),
    },
  });
};
type QueryFnType = typeof getPrograms;

type UseProgramsOptions = {
  filters?: FiltersProgramsType;
  config?: QueryConfig<QueryFnType>;
  isProgramPage?: boolean;
};

export const useGetPrograms = ({ config, isProgramPage }: UseProgramsOptions = {}) => {
  const { gte, lte, state, channel_id, priorities, channel_media_type, paginationValues } =
    useProgramFilters();

  return useQuery<ExtractFnReturnType<QueryFnType>>({
    ...config,
    queryKey: [
      'programs',
      {
        gte,
        channel_id,
        lte,
        state,
        priorities,
        channel_media_type,
        query: paginationValues?.query,
        offset: paginationValues?.offset,
        rowsPerPage: paginationValues?.rowsPerPage,
        sortById: paginationValues?.sortBy?.id,
        sortByOrder: paginationValues?.sortBy?.order,
      },
    ],
    queryFn: () =>
      getPrograms({
        gte,
        lte,
        state,
        channel_id,
        priorities,
        channel_media_type,
        paginationValues,
        isProgramPage,
      }),
  });
};
